/**
 * SOLID Review
 * S — Strong. One concern: bind a view to its view model — made once per mount, attached while the view shows, read by React.
 * O — Strong. Any view model; nothing here names one.
 * L — N/A — a hook.
 * I — Strong. One hook: the snapshot and the model.
 * D — Strong. The view-model contract and the factory's context.
 */
import { useCallback, useEffect, useLayoutEffect, useRef, useState, useSyncExternalStore } from 'react';
import type { IServiceViewModel } from './IServiceViewModel';
import { useViewModelFactory } from './ViewModelProvider';
import type { ViewModelFactory } from './ViewModelFactory';

type TSnapshotOf<TModel> = TModel extends IServiceViewModel<infer TState> ? TState : never;

/** A view that hands its model no props. */
const NO_INPUTS: readonly unknown[] = Object.freeze([]);

/** The facts a commit rendered: the view's inputs and the snapshot. */
interface IPayloadCommittedFacts<TState> {
  readonly inputs: readonly unknown[];
  readonly rendered: TState;
}

/**
 * THE ONE BINDING (frontend-framework-oo P1, 2026-10-29, §28.293) — the only place, with the react-three-fiber views, where a view
 * keeps React state or effects (`lint:budgets --react-allow`).
 *
 *   const [state, model] = useViewModel((views) => views.authRouter());
 *   const [state] = useViewModel((views) => views.liveKitSession(), [roomId, playerId]);   // the view's inputs (P2)
 *
 * The model is made once per mount — `useState`'s initialiser — and kept for the mount's life, StrictMode's simulated remount
 * included; `attach` runs when the view is on the page and `detach` when it leaves (twice each under StrictMode — the contract's
 * pair is idempotent); React reads the model through `useSyncExternalStore`, so its snapshot's identity is the re-render signal.
 */
export function useViewModel<TModel extends IServiceViewModel<unknown>>(
  create: (views: ViewModelFactory) => TModel,
  inputs: readonly unknown[] = NO_INPUTS,
): readonly [TSnapshotOf<TModel>, TModel] {
  const views = useViewModelFactory();
  const [model] = useState(() => create(views));
  useEffect(() => {
    model.attach();
    return () => model.detach();
  }, [model]);
  const subscribe = useCallback((listener: () => void) => model.subscribe(listener), [model]);
  const snapshot = useSyncExternalStore(subscribe, () => model.getSnapshot()) as TSnapshotOf<TModel>;
  // After the commit, with the facts this render used — the model's reactions decide from them (P2, §28.294) — in React's two
  // phases (P3, §28.295): the facts are staged before any passive effect runs (a layout effect), so the effect's cleanup, which
  // React runs in its destroy phase, can hand the model the NEW facts to stop by; its body, in the create phase, starts by them.
  // The inputs are spread into the dependencies: a call site hands the same number every render.
  const staged = useRef<IPayloadCommittedFacts<TSnapshotOf<TModel>>>({ inputs, rendered: snapshot });
  useLayoutEffect(() => {
    staged.current = { inputs, rendered: snapshot };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [model, snapshot, ...inputs]);
  useEffect(() => {
    model.commit?.(inputs, snapshot);
    return () => model.release?.(staged.current.inputs, staged.current.rendered);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [model, snapshot, ...inputs]);
  return [snapshot, model] as const;
}
